import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { formatRelative, formatVnd } from "../../shared/i18n/index.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { serverNow, syncClock } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";
import { listPurchases, type PurchaseListItem } from "../lib/purchase-api.ts";

const filters = ["", "open", "completed", "cancelled"] as const;

/** /me/purchases and /me/sales — dense lists with status chips (08-design.md). */
export function PurchaseList({ kind }: { kind: "purchases" | "sales" }) {
  const { t, locale } = useI18n();
  const { me, loading } = useMe();
  const [params, setParams] = useSearchParams();
  const status = (filters as readonly string[]).includes(params.get("status") ?? "")
    ? (params.get("status") ?? "")
    : "";
  const [items, setItems] = useState<PurchaseListItem[] | null>(null);

  useEffect(() => {
    if (!me) return;
    let live = true;
    listPurchases(kind, status || undefined)
      .then((r) => {
        if (!live) return;
        syncClock(r.server_ts);
        setItems(r.items);
      })
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
  }, [kind, status, me]);

  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to={`/login?next=/me/${kind}`} replace />;
  const now = serverNow();
  return (
    <div className="page">
      <h1 className="h1">
        {kind === "purchases"
          ? t("profile.bought", { n: items?.length ?? 0 })
          : t("profile.sold", { n: items?.length ?? 0 })}
      </h1>
      <div className="chips" role="tablist">
        {filters.map((f) => (
          <button
            key={f}
            role="tab"
            aria-selected={f === status}
            className={`chip${f === status ? " is-on" : ""}`}
            onClick={() => setParams(f ? { status: f } : {})}
          >
            {f === ""
              ? t("common.all")
              : f === "open"
                ? t("purchase.status.pending_contact")
                : t(`purchase.status.${f}`)}
          </button>
        ))}
      </div>
      {items === null ? (
        <p className="muted">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <EmptyState
          text={kind === "purchases" ? t("empty.purchases") : t("empty.sales")}
          cta={{ label: t("nav.browse"), to: "/s" }}
        />
      ) : (
        <ul className="list" data-testid="purchase-list">
          {items.map((i) => (
            <li key={i.id} className="list__row">
              <Link to={`/p/${i.id}`} className="rowlink">
                <div className="rowlink__cover">
                  {i.cover ? <img src={i.cover} alt="" /> : null}
                </div>
                <div className="rowlink__body">
                  <div className="rowlink__title">{i.title}</div>
                  <div className="hint">
                    @{kind === "purchases" ? i.seller_handle : i.buyer_handle} · {i.quantity} × ·{" "}
                    {formatVnd(i.total)} · {formatRelative(i.created_at, now, locale)}
                  </div>
                </div>
                <span className={`tag${i.status === "completed" ? " tag--ok" : ""}`}>
                  {t(`purchase.status.${i.status}`)}
                  {i.problem_open ? " · !" : ""}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
